import cloneDeep from "lodash/cloneDeep";
import isNull from "lodash/isNull";
import mergeWith from "lodash/mergeWith";
import { deepIntersectionEquals } from "./equal";
import { DeepPartial } from "./type";

export const objectEntries = Object.entries as <T>(
  object: T,
) => [keyof T, T[keyof T]][];

/**
 * Returns a shallow copy of `obj` with `keys` removed. Type-safe wrapper
 * around the spread/destructure pattern.
 */
export function omit<T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Omit<T, K> {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result;
}

/**
 * Returns a shallow copy of `obj` containing only the listed `keys`.
 */
export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
}

export function objectMapValues<A, B>(
  obj: A,
  fn: (value: A[keyof A], key: keyof A) => B,
): { [K in keyof A]: B } {
  const result = {} as { [K in keyof A]: B };
  for (const [key, value] of objectEntries(obj)) {
    result[key] = fn(value, key);
  }
  return result;
}

export function deepCopyObject<T extends object>(obj: T): T {
  return cloneDeep(obj);
}

export function deepMergeObjectsOverwriteNull<T extends object>(
  target: T,
  source: T,
): T {
  return mergeWith(target, source, (objValue, srcValue) => {
    return isNull(objValue) ? srcValue : objValue;
  });
}

type RecursivelyReplaceNullWithUndefined<T> = T extends null
  ? undefined
  : T extends Date
    ? T
    : {
        [K in keyof T]: T[K] extends (infer U)[]
          ? RecursivelyReplaceNullWithUndefined<U>[]
          : RecursivelyReplaceNullWithUndefined<T[K]>;
      };

export function nullsToUndefined<T>(
  obj: T,
): RecursivelyReplaceNullWithUndefined<T> {
  if (obj === null) {
    return undefined as any;
  }

  // object check based on: https://stackoverflow.com/a/51458052/6489012
  if (obj != null && obj.constructor.name === "Object") {
    for (const key in obj) {
      obj[key] = nullsToUndefined(obj[key]) as any;
    }
  }
  return obj as any;
}

/**
 * convertUndefinedIfDefault is a utility function that converts a value to null if it matches any of the default values.
 *
 * Useful for forms where we only want to save the input values if they differ from the the default blank input values AND also if it's the same as the default evaluated value. e.g. a form for the Checkout Button text could use this function to convert either "" or "Checkout+" evaluates to undefined if it since those are defaults.
 */
export const convertUndefinedIfDefault = <T>(
  _input: T,
  defaultsList: any[],
): DeepPartial<T> | undefined => {
  const input = cloneDeep(_input);

  if (typeof input !== "object" || input === null) {
    for (const defaultObj of defaultsList) {
      if (defaultObj === input) {
        return undefined;
      }
    }
    return input as DeepPartial<T>;
  }

  const result = {} as Record<keyof T, unknown>;

  for (const key in input) {
    const convertedValue = convertUndefinedIfDefault(
      input[key] as Record<string, unknown>,
      defaultsList
        .map((defaultObj) => defaultObj[key])
        .filter((defaultObj) => defaultObj != null) as Record<
        string,
        unknown
      >[],
    );
    result[key] = convertedValue;
  }

  return result as DeepPartial<T>;
};

function _deepCopyWithoutNullish<T extends object>(
  input: T,
  collapseRootIfEmptyObject = false,
): T | DeepPartial<T> | undefined {
  if (input == null) {
    return undefined;
  }

  if (Array.isArray(input)) {
    return input;
  }

  if (typeof input === "object") {
    const result: Record<string | number | symbol, unknown> = {};
    for (const [key, value] of Object.entries(input)) {
      const copied = _deepCopyWithoutNullish(value, true);

      if (copied !== undefined) {
        result[key] = copied;
      }
    }

    return Object.keys(result).length === 0 && collapseRootIfEmptyObject
      ? undefined
      : (result as DeepPartial<T> | T);
  }

  return input;
}

/**
 * Recursively deletes any object's properties who's values are nullish. Child objects who's properties are all deleted
 * will get collapsed to also become defined. Optionally, the root object can also be collapsed to undefined
 * if all descendants evaluate to nullish. Arrays and descendants of arrays ignored.
 * @param input
 * @param collapseRootIfEmptyObject
 * @returns
 */
export function deepCopyWithoutNullish<T extends object>(
  input: T,
): T | DeepPartial<T> {
  return _deepCopyWithoutNullish(input, false) as T | DeepPartial<T>; // Have to cast because of the collapseRootIfEmptyObject parameter. It will not be undefined.
}

export function removeNullishFromObjectInPlace<T extends object>(input: T): T {
  if (input == null) {
    return input;
  }

  for (const key in input) {
    const value = input[key];
    if (value == null) {
      delete input[key];
    } else if (Array.isArray(value)) {
      input[key] = value
        .map((item) => removeNullishFromObjectInPlace(item))
        .filter(
          (i) =>
            i != undefined &&
            !(
              typeof i === "object" &&
              i.constructor === Object &&
              Object.keys(i).length === 0
            ),
        ) as any;
    } else if (typeof value === "object") {
      removeNullishFromObjectInPlace(value);
      // Have to exclude objects from getting deleted that are not empty objects (like Date)
      if (Object.keys(value).length === 0 && value.constructor === Object) {
        delete input[key];
      }
    }
  }
  return input;
}

type Path = string; // e.g. "property.subProperty.subSubProperty"
/**
 * setObjectProperty is a utility function that sets a property on an object at a given path.
 * @param obj - The object to set the property on.
 * @param path - e.g. "property.subProperty.subSubProperty"
 * @param value - The value to set the property to e.g. true
 * @returns The object with the property set.
 */
export function setObjectPath<T extends object, V = any>(
  obj: T,
  path: Path,
  value: V,
): T {
  const keys = path.split(".");
  let current: any = obj;

  for (const [index, key] of keys.entries()) {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      if (typeof current[key] !== "object" || current[key] === null) {
        current[key] = {};
      }
      current = current[key];
    }
  }

  return obj;
}

/**
 * getObjectPath is a utility function that gets a property on an object at a given path.
 * @param obj - The object to get the property from.
 * @param path - e.g. "property.subProperty.subSubProperty"
 * @returns The value of the property at the given path.
 */
export function getObjectPath<T, R = any>(obj: T, path: Path): R | undefined {
  const keys = path.split(".");

  let current: any = obj;
  for (const key of keys) {
    if (current == null || typeof current !== "object") {
      return undefined;
    }
    current = current[key];
  }

  return current as R;
}

/**
 * redactObjectValues is a utility function that replaces the values of the keys in the object with the values of the keys in the replacement object.
 * uses could be to log objects with sensitive information, or to log objects that have values that flood the logs (like data URLs, html, etc.)
 * @param obj - The object to replace the values of the keys in.
 * @param redactions - A map of keys to replace the values of to the values to replace them with.
 * @returns The object with the values of the keys replaced.
 */
export function redactObjectValues(
  obj: object,
  redactions: Map<string, string>,
): object {
  if (Array.isArray(obj)) {
    return obj.map((item) => redactObjectValues(item, redactions));
  } else if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (redactions.has(key)) {
          return [key, redactions.get(key)];
        }
        return [key, redactObjectValues(value, redactions)];
      }),
    );
  }
  return obj;
}

export function deepStripNullAndEmptyArrays<T extends {}>(object: T): T {
  return Object.fromEntries(
    Object.entries(object).flatMap(([key, value]) => {
      if (value === null || (Array.isArray(value) && value.length === 0)) {
        return [];
      }
      if (Array.isArray(value)) {
        const filtered = value
          .filter((item) => item !== null)
          .map((item) =>
            typeof item === "object" ? deepStripNullAndEmptyArrays(item) : item,
          );
        if (filtered.length > 0) {
          return [[key, filtered]];
        }
      } else if (typeof value === "object" && value.constructor === Object) {
        return [[key, deepStripNullAndEmptyArrays(value)]];
      } else {
        return [[key, value]];
      }
      return [];
    }),
  ) as T;
}

/**
 * Recursively traverses an object and applies a callback to all string values.
 * Returns a new object without mutating the original (immutable version).
 *
 * @param obj - The object to traverse
 * @param callback - Function to call on each string value. The return value replaces the original string.
 * @returns A new object with transformed string values
 *
 * @example
 * const data = { name: "  John  ", address: { city: "  NYC  " } };
 * const cleaned = runCallbackOnStrings(data, (str) => str.trim());
 * // cleaned is { name: "John", address: { city: "NYC" } }
 * // data is unchanged
 */
export function runCallbackOnStrings<T>(
  obj: T,
  callback: (value: string) => string,
): T {
  if (obj == null) {
    return obj;
  }

  if (typeof obj === "string") {
    return callback(obj) as T;
  }

  if (typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => runCallbackOnStrings(item, callback)) as T;
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === "string"
        ? callback(value)
        : runCallbackOnStrings(value, callback),
    ]),
  ) as T;
}

/**
 * Returns an array of top-level keys where the values differ between two objects.
 * Uses deep equality comparison to detect differences in nested structures.
 * @param a - First object to compare
 * @param b - Second object to compare
 * @returns Array of keys that have different values between the two objects
 */
export function getDiffKeys<T extends object>(a: T, b: T): (keyof T)[] {
  const allKeys = new Set<keyof T>([
    ...Object.keys(a),
    ...Object.keys(b),
  ] as (keyof T)[]);

  return Array.from(allKeys).filter(
    (key) => !deepIntersectionEquals(a[key], b[key]),
  );
}
